import moment from "moment";

export const DEFAULT_KM_PER_DAY = 300;
export const WEEKEND_BILLABLE_DAYS = 2;
export const WEEKEND_INCLUDED_KM = 700;
/** @deprecated Preferir match por DEFAULT_PROMO_RULES */
export const WEEKEND_PICKUP_TIME = "15:00:00";
export const WEEKEND_DROPOFF_TIME = "15:00:00";
export const WEEKEND_PICKUP_WEEKDAY = 5;
export const WEEKEND_DROPOFF_WEEKDAY = 1;
export const LOW_SEASON_PCT = 20;
export const LOW_SEASON_MIN_DAYS = 2;
export const LOW_SEASON_MONTHS = [12, 1, 2, 3];

/** Parsea months CSV o array → enteros 1–12. */
export const parseLowSeasonMonths = months => {
  if (Array.isArray(months)) {
    return months.map(Number).filter(n => n >= 1 && n <= 12);
  }
  if (months == null || months === "") return [];
  return String(months)
    .split(/[,\s]+/)
    .map(Number)
    .filter(n => n >= 1 && n <= 12);
};

/** Espejo seed BD rental_low_season_rules (B2C + agency). */
const DEFAULT_LOW_SEASON_RULES_B2C = [
  {
    id: null,
    code: "low_season",
    name: "Temporada baja dic-mar",
    active: true,
    discount_pct: LOW_SEASON_PCT,
    min_billable_days: LOW_SEASON_MIN_DAYS,
    months: "12,1,2,3",
    apply_to_moto: true,
    apply_to_gps: true,
    apply_to_vip: false,
    applies_to_agency: false,
    priority: 100,
    valid_from: null,
    valid_to: null,
  },
];
export const DEFAULT_LOW_SEASON_RULES = DEFAULT_LOW_SEASON_RULES_B2C.concat(
  DEFAULT_LOW_SEASON_RULES_B2C.map(r => ({
    ...r,
    code: "low_season_agency",
    name: `${r.name} (agency)`,
    applies_to_agency: true,
  }))
);

/** Espejo seed BD rental_promo_rules (B2C + agency). */
const DEFAULT_PROMO_RULES_B2C = [
  {
    code: "weekend_package",
    pickup_weekday: WEEKEND_PICKUP_WEEKDAY,
    dropoff_weekday: WEEKEND_DROPOFF_WEEKDAY,
    pickup_time: "18:00:00",
    dropoff_time: "15:00:00",
    calendar_span_days: 3,
    billable_days: WEEKEND_BILLABLE_DAYS,
    included_km: WEEKEND_INCLUDED_KM,
    applies_to_agency: false,
    priority: 100,
    active: true,
  },
  {
    code: "weekend_package",
    pickup_weekday: WEEKEND_PICKUP_WEEKDAY,
    dropoff_weekday: WEEKEND_DROPOFF_WEEKDAY,
    pickup_time: "18:00:00",
    dropoff_time: "10:00:00",
    calendar_span_days: 3,
    billable_days: WEEKEND_BILLABLE_DAYS,
    included_km: WEEKEND_INCLUDED_KM,
    applies_to_agency: false,
    priority: 100,
    active: true,
  },
  {
    code: "weekend_package",
    pickup_weekday: WEEKEND_PICKUP_WEEKDAY,
    dropoff_weekday: WEEKEND_DROPOFF_WEEKDAY,
    pickup_time: "15:00:00",
    dropoff_time: "10:00:00",
    calendar_span_days: 3,
    billable_days: WEEKEND_BILLABLE_DAYS,
    included_km: WEEKEND_INCLUDED_KM,
    applies_to_agency: false,
    priority: 100,
    active: true,
  },
  {
    code: "weekend_package",
    pickup_weekday: WEEKEND_PICKUP_WEEKDAY,
    dropoff_weekday: WEEKEND_DROPOFF_WEEKDAY,
    pickup_time: "15:00:00",
    dropoff_time: "15:00:00",
    calendar_span_days: 3,
    billable_days: WEEKEND_BILLABLE_DAYS,
    included_km: WEEKEND_INCLUDED_KM,
    applies_to_agency: false,
    priority: 100,
    active: true,
  },
];
export const DEFAULT_PROMO_RULES = DEFAULT_PROMO_RULES_B2C.concat(
  DEFAULT_PROMO_RULES_B2C.map(r => ({
    ...r,
    code: "weekend_package_agency",
    applies_to_agency: true,
  }))
);

let cachedPromoRules = null;
let cachedLowSeasonRules = null;

const normalizeTime = time => {
  if (time == null || time === "") return "";
  const raw = String(time).trim();
  let normalized = "";
  if (/^\d{1,2}$/.test(raw)) normalized = `${raw.padStart(2, "0")}:00:00`;
  else if (/^\d{1,2}:\d{2}$/.test(raw)) normalized = `${raw}:00`;
  else {
    const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      normalized = `${match[1].padStart(2, "0")}:${match[2]}:${match[3] || "00"}`;
    } else {
      normalized = raw;
    }
  }
  // Booking ya no ofrece 19:00; reglas/históricos 19 ≡ 18
  if (normalized === "19:00:00") return "18:00:00";
  return normalized;
};

/**
 * Fecha calendario YYYY-MM-DD sin sesgo de TZ local.
 * Booking API suele mandar `2026-10-16T00:00:00.000Z` → en UTC-5 moment()
 * lo baja a jueves y rompe match weekend (vie→lun).
 */
export const toDateKey = date => {
  if (date == null || date === "") return null;
  if (Object.prototype.toString.call(date) === "[object Date]") {
    const m = moment.utc(date);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
  }
  const raw = String(date).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = moment.utc(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

/** Misma regla histórica 10:00/18:00 del admin. */
export const occupancyDays = (date1, time1, date2, time2) => {
  const d1 = toDateKey(date1);
  const d2 = toDateKey(date2);
  if (!d1 || !d2) return 0;
  let days = moment.utc(d2).diff(moment.utc(d1), "days");
  const t1 = normalizeTime(time1);
  const t2 = normalizeTime(time2);
  if (t1 === "10:00:00" && t2 === "18:00:00") days += 1;
  else if (t1 === "18:00:00" && t2 === "10:00:00") days -= 1;
  return days || 0;
};

export const getActivePromoRules = () => {
  if (cachedPromoRules === null) {
    return DEFAULT_PROMO_RULES;
  }
  return cachedPromoRules;
};

export const setPromoRulesCache = rules => {
  if (rules === null || rules === undefined) {
    cachedPromoRules = null;
    return;
  }
  cachedPromoRules = Array.isArray(rules) ? rules : null;
};

export const getActiveLowSeasonRules = () => {
  if (cachedLowSeasonRules === null) {
    return DEFAULT_LOW_SEASON_RULES;
  }
  return cachedLowSeasonRules;
};

export const setLowSeasonRulesCache = rules => {
  if (rules === null || rules === undefined) {
    cachedLowSeasonRules = null;
    return;
  }
  cachedLowSeasonRules = Array.isArray(rules) ? rules : null;
};

const isRuleValidOnDate = (rule, start) => {
  if (!start || !start.isValid()) return false;
  if (rule.valid_from) {
    const from = moment(rule.valid_from);
    if (from.isValid() && start.isBefore(from, "day")) return false;
  }
  if (rule.valid_to) {
    const to = moment(rule.valid_to);
    if (to.isValid() && start.isAfter(to, "day")) return false;
  }
  return true;
};

export const matchPromoRule = (rentalLike = {}, options = {}) => {
  const date1 = toDateKey(rentalLike.date_1);
  const date2 = toDateKey(rentalLike.date_2);
  const time1 = normalizeTime(rentalLike.time_1);
  const time2 = normalizeTime(rentalLike.time_2);
  if (!date1 || !date2 || !time1 || !time2) return null;

  const start = moment.utc(date1);
  const end = moment.utc(date2);
  if (!start.isValid() || !end.isValid()) return null;
  const span = end.diff(start, "days");
  const startWd = start.isoWeekday();
  const endWd = end.isoWeekday();
  const forAgency = options.forAgency === true;

  const rules = (options.rules || getActivePromoRules())
    .filter(r => r && (r.active === true || r.active === 1))
    .slice()
    .sort((a, b) => (Number(a.priority) || 100) - (Number(b.priority) || 100));

  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i];
    const ruleForAgency = rule.applies_to_agency === true || rule.applies_to_agency === 1;
    if (forAgency !== ruleForAgency) continue;

    const rulePickup = normalizeTime(rule.pickup_time);
    const ruleDropoff = normalizeTime(rule.dropoff_time);
    if (time1 !== rulePickup || time2 !== ruleDropoff) continue;
    if (rule.pickup_weekday != null && Number(rule.pickup_weekday) !== startWd) continue;
    if (rule.dropoff_weekday != null && Number(rule.dropoff_weekday) !== endWd) continue;
    const ruleSpan = rule.calendar_span_days != null ? Number(rule.calendar_span_days) : 3;
    if (span !== ruleSpan) continue;
    return rule;
  }
  return null;
};

export const isWeekendSlot = (date1, time1, date2, time2) =>
  matchPromoRule({ date_1: date1, time_1: time1, date_2: date2, time_2: time2 }) != null;

/** date_1 y date_2 (si options.date2) deben caer en months de la regla. */
export const matchLowSeasonRule = (date1, billableDays, options = {}) => {
  const key = toDateKey(date1);
  if (!key) return null;
  const start = moment.utc(key);
  if (!start.isValid()) return null;
  const month1 = start.month() + 1;
  const forAgency = options.forAgency === true;
  const days = Number(billableDays) || 0;

  let month2 = null;
  if (options.date2 != null && options.date2 !== "") {
    const key2 = toDateKey(options.date2);
    if (!key2) return null;
    const end = moment.utc(key2);
    if (!end.isValid()) return null;
    month2 = end.month() + 1;
  }

  const rules = (options.rules || getActiveLowSeasonRules())
    .filter(r => r && (r.active === true || r.active === 1 || r.active === undefined))
    .slice()
    .sort((a, b) => (Number(a.priority) || 100) - (Number(b.priority) || 100));

  for (let i = 0; i < rules.length; i += 1) {
    const rule = rules[i];
    const ruleForAgency = rule.applies_to_agency === true || rule.applies_to_agency === 1;
    if (forAgency !== ruleForAgency) continue;
    if (!isRuleValidOnDate(rule, start)) continue;
    const months = parseLowSeasonMonths(rule.months);
    if (months.indexOf(month1) === -1) continue;
    if (month2 != null && months.indexOf(month2) === -1) continue;
    if (!options.ignoreMinDays && days < Number(rule.min_billable_days || 0)) continue;
    return rule;
  }
  return null;
};

export const isLowSeasonDate = date1 =>
  matchLowSeasonRule(date1, 999, { ignoreMinDays: true }) != null;

export const applyLowSeasonDiscount = (amount, pct) => {
  const value = Number(amount) || 0;
  const percent = Number(pct) || 0;
  if (percent <= 0) return Math.round(value * 100) / 100;
  return Math.round(value * (1 - percent / 100) * 100) / 100;
};

/** −% temporada baja: moto/gps/one_way/other sí; vip nunca. */
export const applyLowSeasonDiscountFor = (amount, pctOrSnap, target) => {
  const snap = pctOrSnap && typeof pctOrSnap === "object" ? pctOrSnap : null;
  const pct = snap
    ? Number(snap.low_season_pct) || 0
    : Number(pctOrSnap) || 0;
  if (target === "vip") return Math.round((Number(amount) || 0) * 100) / 100;
  if (pct <= 0) return Math.round((Number(amount) || 0) * 100) / 100;
  if (snap) {
    if (target === "moto" && snap.low_season_apply_moto === false) {
      return Math.round((Number(amount) || 0) * 100) / 100;
    }
    if (target === "gps" && snap.low_season_apply_gps === false) {
      return Math.round((Number(amount) || 0) * 100) / 100;
    }
  }
  return applyLowSeasonDiscount(amount, pct);
};

/**
 * @param {object} rentalLike date_1/time_1/date_2/time_2 + opcionales from_agency
 * @param {{ applyPromo?: boolean, forAgency?: boolean, rules?: Array, lowSeasonRules?: Array }} options
 */
export const buildRentalPromoSnapshot = (rentalLike = {}, options = {}) => {
  const forAgency =
    options.forAgency != null
      ? options.forAgency === true
      : rentalLike.from_agency === true || rentalLike.from_agency === 1;
  const applyPromo = options.applyPromo !== false;

  const occ = occupancyDays(
    rentalLike.date_1,
    rentalLike.time_1,
    rentalLike.date_2,
    rentalLike.time_2
  );

  if (!applyPromo) {
    return {
      is_weekend: false,
      occupancy_days: occ,
      billable_days: occ,
      included_km: occ > 0 ? occ * DEFAULT_KM_PER_DAY : null,
      low_season_pct: 0,
      low_season_apply_moto: false,
      low_season_apply_gps: false,
      low_season_apply_vip: false,
      moto_days: occ,
      extras_days: occ,
      promo_rule_id: null,
    };
  }

  const matched = matchPromoRule(rentalLike, { ...options, forAgency });
  const weekend = matched != null;
  const billableDays = weekend ? Number(matched.billable_days) : occ;
  const includedKm = weekend
    ? Number(matched.included_km)
    : occ > 0
      ? occ * DEFAULT_KM_PER_DAY
      : null;
  // Obs. 2: GPS/VIP usan días promo cuando hay match.
  const extrasDays = weekend ? billableDays : occ;
  const lsMatch = matchLowSeasonRule(rentalLike.date_1, billableDays, {
    rules: options.lowSeasonRules,
    date2: rentalLike.date_2,
    forAgency,
  });
  const lowSeasonPct = lsMatch ? Number(lsMatch.discount_pct) || 0 : 0;

  return {
    is_weekend: weekend,
    occupancy_days: occ,
    billable_days: billableDays,
    included_km: includedKm,
    low_season_pct: lowSeasonPct,
    // RN-R36: con match, moto/GPS −% por defecto; VIP nunca salvo flag explícito.
    low_season_apply_moto: lsMatch
      ? !(lsMatch.apply_to_moto === false || lsMatch.apply_to_moto === 0)
      : false,
    low_season_apply_gps: lsMatch
      ? !(lsMatch.apply_to_gps === false || lsMatch.apply_to_gps === 0)
      : false,
    low_season_apply_vip: !!(lsMatch && (lsMatch.apply_to_vip === true || lsMatch.apply_to_vip === 1)),
    moto_days: billableDays,
    extras_days: extrasDays,
    promo_rule_id: matched && matched.id != null ? matched.id : null,
  };
};

/** Opciones de hora admin (process / edit). Solo 10 / 15 / 18. */
export const RENTAL_TIME_OPTIONS = [
  { id: "10:00:00", name: "10:00 (Morning)" },
  { id: "15:00:00", name: "15:00 (Afternoon - weekend)" },
  { id: "18:00:00", name: "18:00 (Evening)" },
];

export const formatRentalTimeLabel = time => {
  const t = normalizeTime(time);
  if (t === "10:00:00") return "morning";
  if (t === "15:00:00") return "15:00 afternoon-weekend";
  if (t === "18:00:00") return "evening";
  return t || "—";
};



// WEBPACK FOOTER //
// ./src/util/rentalPromo.js